import { useAppointmentDetail } from '../../../entities/appointment';
import { useWorkOrderDetail } from '../../../entities/work-order';

interface Input {
  /** `plan.mode` / `plan.workOrderId` of `useDispositionMode`. */
  planMode: string | null;
  planWorkOrderId: string | null;
  /** `source.workOrderId` of `useRescheduleSource` — set when an existing Termin is moved. */
  sourceWorkOrderId: string | null;
  /** The appointment form, as `useUpsertAppointment` reports it. */
  appointmentForm: {
    isOpen: boolean;
    mode: 'create' | 'edit' | null;
    appointmentId: number | null;
    workOrderId: string | null;
  };
  /** A task has no Gewerk; while its form is open the board stays as it is. */
  taskFormOpen: boolean;
}

/**
 * The Gewerk of the Auftrag the dispatcher is planning a Termin for, or `null` when there is none.
 *
 * `null` is the "leave the board alone" answer: no appointment in play, a task form open, the Auftrag
 * without a Gewerk, or the lookup still in flight. Precedence follows what the dispatcher is looking at:
 *
 * 1. a task form → `null`
 * 2. the form in **edit** → that Termin's own Auftrag. `upsert-appointment-work-order-id` is not read
 *    here: `openEdit` does not clear it, so after a create it can name an unrelated Auftrag.
 * 3. the form in **create** → the Auftrag in the URL
 * 4. planning a new Termin or moving one → the plan's / the moved Termin's Auftrag
 *
 * Every hook is called on every render and only the id decides which one is enabled. The queries
 * share their keys with `PlanningBar` and `UpsertAppointmentModal`, so this costs no extra request.
 */
export function usePlanningGewerkId({
  planMode,
  planWorkOrderId,
  sourceWorkOrderId,
  appointmentForm,
  taskFormOpen,
}: Input): number | null {
  const editing = appointmentForm.isOpen && appointmentForm.mode === 'edit';
  const creating = appointmentForm.isOpen && appointmentForm.mode === 'create';

  const appointment = useAppointmentDetail(
    !taskFormOpen && editing ? (appointmentForm.appointmentId ?? undefined) : undefined,
  );

  const planned =
    !taskFormOpen && !appointmentForm.isOpen && planMode === 'schedule-appointment'
      ? (planWorkOrderId ?? sourceWorkOrderId ?? undefined)
      : undefined;
  const workOrderId = !taskFormOpen && creating ? (appointmentForm.workOrderId ?? undefined) : planned;
  const workOrder = useWorkOrderDetail(workOrderId);

  if (taskFormOpen) return null;
  if (editing) return appointment.data?.workOrder?.external?.gewerkId ?? null;
  return workOrder.data?.external?.gewerkId ?? null;
}
